import React, { ReactElement, useContext } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PersonContext, contextConfig } from '../contexts/PersonContext'
import InfiniteScroll, { infiniteScrollConfig, InfiniteScrollHeader } from '../common/InfiniteScroll'
import { InfiniteScrollParamsRaw } from '../common/InfiniteScroll.d'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faArrowCircleRight, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { formatDate } from '../helpers/format'
import { EntryContext } from '../contexts/EntryContext'
library.add(faArrowCircleRight, faInfoCircle)

export const PersonThumbnailListWithField = (): ReactElement => {
    const { state } = useContext(PersonContext)
    const { state: entryState } = useContext(EntryContext)
    const rawParams = useParams<InfiniteScrollParamsRaw>()
    if (!rawParams.field) return <></>
    const paramsHeader = { ...rawParams, field: rawParams.field }
    const span = paramsHeader.field === 'span' ? entryState.spans?.[parseInt(paramsHeader.value || '')] : undefined
    const params = span
        ? { ...paramsHeader,
            field: 'date',
            comparison: 'between' as const,
            value: `${formatDate(span.startTimestamp * 1000)};${formatDate(span.endTimestamp * 1000)}`
        }
        : paramsHeader
    const infiniteScrollProps = infiniteScrollConfig(contextConfig, state, params)

    return (
        <div className="thumbnail-list person-thumbnail-list">
            <InfiniteScrollHeader { ...paramsHeader } personLinkText="Linked to" />
            <InfiniteScroll { ...infiniteScrollProps } key={ infiniteScrollProps.key } />
        </div>)
}

export const PersonThumbnailListFromPosition = (): ReactElement => {
    const { state } = useContext(PersonContext)
    const { start } = useParams<{ start?: string }>()
    const infiniteScrollProps = infiniteScrollConfig({
        ...contextConfig
    }, state, { field: 'id', comparison: 'above', value: start })
    return (
        <div className="thumbnail-list person-thumbnail-list">
            <InfiniteScroll { ...infiniteScrollProps } key={ infiniteScrollProps.key } />
        </div>)
}

const TopPeopleThumbnailListTitle = (props: { type: 'name' | 'hashtag' | 'handle' | 'all', maxItems: number }) =>
    <h3>
        Top {props.maxItems} {props.type === 'all' ? 'overall' : `${props.type}s`}&nbsp;
        <Link to={ props.type === 'all' ? '/people/by/count' : `/people/by/type/${props.type}` }>
            <FontAwesomeIcon icon="arrow-circle-right" />
        </Link>
    </h3>

export const TopPeopleThumbnailList = (props: { type: 'name' | 'hashtag' | 'handle' | 'all', maxItems: number }): ReactElement => {
    const { state } = useContext(PersonContext)
    const infiniteScrollProps = infiniteScrollConfig(
        { ...contextConfig, renderDetails: undefined },
        state,
        props.type === 'all' ? { field: 'count' } : { field: 'type', value: props.type })
    return (
        <div className="thumbnail-list person-thumbnail-list">
            <InfiniteScroll
                maxItems={ props.maxItems }
                optionalTitle={ <TopPeopleThumbnailListTitle maxItems={ props.maxItems } type={ props.type } /> }
                { ...infiniteScrollProps }
                key={ infiniteScrollProps.key } />
        </div>)
}

export const RecentPeopleThumbnailList = (props: { date: number, until?: number, maxItems: number, spanId?: number }): ReactElement => {
    const { state } = useContext(PersonContext)
    const infiniteScrollProps = infiniteScrollConfig(
        { ...contextConfig, renderDetails: undefined },
        state,
        props.until
            ? { field: 'date', comparison: 'between', value: `${formatDate(props.date * 1000)};${formatDate(props.until * 1000)}` }
            : { field: 'date', comparison: 'after', value: formatDate(props.date * 1000) })
    const link = props.spanId
        ? `/people/by/span/${props.spanId}`
        : props.until
            ? `/people/by/date/between/${formatDate(props.date * 1000)};${formatDate(props.until * 1000)}`
            : `/people/by/date/after/${formatDate(props.date * 1000)}`
    return (
        <div className="thumbnail-list person-thumbnail-list">
            <InfiniteScroll
                maxItems={ props.maxItems }
                optionalTitle={ <h3>Top {props.maxItems} in this time period&nbsp;
                    <Link to={ link }><FontAwesomeIcon icon="arrow-circle-right" /></Link>
                </h3> }
                { ...infiniteScrollProps }
                key={ infiniteScrollProps.key } />
        </div>)
}

export default PersonThumbnailListFromPosition
